import React from 'react'
import styled from 'styled-components'

import { Skeleton, Tooltip } from '@blade/ui-kit'
import { Trend } from '@root/entities/trend'
import { NumericFormat, VolumeShortFormatted } from '@root/shared/core/format'
import { useOptionalAtom } from '@root/shared/lib'

import { METRICS_COLOR_MAP, type MetricsTheme } from './constants'

import type { PairMetrics } from '@root/entities/pair'
import type { PairPeriodStatsModel } from '@root/entities/pair/atoms/reatom-pair-metrics'

type TradingPairMetricsProps = {
  interval: PairMetrics.MetricsSpan
  onClick: (interval: PairMetrics.MetricsSpan) => void
  active: boolean
  expanded: boolean
  metricsTheme: MetricsTheme
  isLast: boolean
  stats: PairPeriodStatsModel | undefined
}

export const TradingPairMetricsTableTab: React.FC<TradingPairMetricsProps> = ({
  interval,
  stats,
  active,
  onClick,
  expanded,
  metricsTheme,
  isLast,
}) => {
  const priceChangePercents = useOptionalAtom(stats?.priceChange)
  const handleClick = () => onClick(interval)
  const volumeUsd = useOptionalAtom(stats?.volumeUsd)

  if (!priceChangePercents) {
    return (
      <Root $active={active} $expanded={expanded}>
        <Skeleton height={16} paddingBlock={3} width={30} />
        <Skeleton height={16} paddingBlock={3} width={60} />
      </Root>
    )
  }

  const trend = Trend.of(priceChangePercents.toNumber())

  const formatted = NumericFormat.formatPreset(
    priceChangePercents,
    'percents',
    { forceSign: true },
  )

  const formattedFull = NumericFormat.formatPreset(
    priceChangePercents,
    'full',
    { mantissa: 2, trimMantissa: true },
  )

  return (
    <Root $active={active} $expanded={expanded} onClick={handleClick}>
      <SpanContainer>
        <SpanLabel data-active={active}>{interval.toUpperCase()}</SpanLabel>
        {priceChangePercents.isZero() ? undefined : (
          <Tooltip
            delayDuration={0}
            disableHoverableContent
            align={isLast ? 'end' : 'start'}
            content={
              <ChangeLabel $trend={trend} $metricsTheme={metricsTheme}>
                {formattedFull}%
              </ChangeLabel>
            }
          >
            <ChangeLabel $trend={trend} $metricsTheme={metricsTheme}>
              {formatted}%
            </ChangeLabel>
          </Tooltip>
        )}
      </SpanContainer>
      <VolumeLabel>
        {volumeUsd === undefined ? (
          <span>-</span>
        ) : (
          <VolumeShortFormatted volume={volumeUsd} />
        )}
      </VolumeLabel>
    </Root>
  )
}

const Root = styled.div<{ $active: boolean; $expanded: boolean }>`
  background: ${({ $active, $expanded }) =>
    $active && $expanded ? 'var(--backgroundA-3)' : 'transparent'};
  cursor: pointer;

  display: grid;
  justify-content: start;
  justify-items: start;
  align-content: center;

  border-radius: 9px;
  padding: 8px 13px;
`

const SpanLabel = styled.span`
  font-size: 11px;
  font-weight: 600;
  line-height: 16px;
  color: var(--text-9);
  &[data-active='true'] {
    color: var(--text-12);
  }
`

const ChangeLabel = styled.span<{
  $metricsTheme: MetricsTheme
  $trend: Trend.Trend
}>`
  color: ${({ $trend, $metricsTheme }) =>
    METRICS_COLOR_MAP[$metricsTheme][$trend]};
  font-size: 11px;
  font-weight: 600;
`

const SpanContainer = styled.div`
  display: grid;
  grid-auto-flow: column;
  align-items: center;
  justify-content: start;
  gap: 4px;
`

const VolumeLabel = styled.span`
  color: var(--text-12);
  font-size: 11px;
  font-weight: 500;
  line-height: 16px;
`
